/**
 * Silent token refresh using Google Identity Services.
 * 
 * Uses google.accounts.id.prompt() with auto_select to get a fresh
 * ID token without user interaction, as long as Google's session
 * cookie is still valid (persists weeks across browser restarts).
 */

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const REFRESH_TIMEOUT = 10_000; // 10 seconds max wait

/**
 * Attempt to silently get a fresh Google ID token.
 * Returns the new credential string, or null if silent refresh failed.
 */
export function silentRefreshToken(): Promise<string | null> {
  return new Promise((resolve) => {
    const google = (window as any).google;
    if (!google?.accounts?.id) {
      console.log('Google Identity Services not loaded');
      resolve(null);
      return;
    }

    let resolved = false;
    const done = (value: string | null) => {
      if (resolved) return;
      resolved = true;
      resolve(value);
    };

    // Timeout — don't wait forever
    const timer = setTimeout(() => {
      console.log('Silent refresh timed out');
      done(null);
    }, REFRESH_TIMEOUT);

    google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      auto_select: true,
      callback: (response: { credential?: string }) => {
        clearTimeout(timer);
        if (response.credential) {
          done(response.credential);
        } else {
          done(null);
        }
      },
      cancel_on_tap_outside: false,
    });

    // prompt() triggers the One Tap flow — with auto_select it returns
    // immediately if there's a single previously-authorized account
    google.accounts.id.prompt((notification: any) => {
      // Prompt was dismissed or suppressed — no credential coming
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        clearTimeout(timer);
        const reason = notification.getNotDisplayedReason?.() || notification.getSkippedReason?.() || 'unknown';
        console.log('Silent refresh not available:', reason);
        done(null);
      }
    });
  });
}
